import { useState } from 'react';
import { Stack, Heading, Icon, Text } from '@chakra-ui/react';
import { AiFillCheckCircle, AiFillCloseCircle } from 'react-icons/ai';
import { ItemModel, ListingModel, ArtistModel } from 'models';
import { ItemCard, ArtistCard } from 'components';
import FilePondUploader from 'components/Uploader/FilepondUploader/FilepondUploader';
import Wizard from '../Wizard';
import { Cloneable } from 'models/Utils/Cloneable';

export type MigrateEntityType = 'item' | 'listing' | 'artist';

interface MigrateImageWizardProps {
  entity: ItemModel | ListingModel | ArtistModel;
  entityType: MigrateEntityType;
  isLoading?: boolean;
  onSubmit?: (entity: ItemModel | ListingModel | ArtistModel, newImageUrl: string) => void;
  hasErrors?: boolean;
}

export default function MigrateImageWizard({
  entity,
  entityType,
  isLoading = false,
  onSubmit = () => {},
  hasErrors = false
}: MigrateImageWizardProps) {
  const [newImageUrl, setNewImageUrl] = useState<string>('');
  const [hasImageChanged, setHasImageChanged] = useState(false);

  const handleFileProcess = (url: string) => {
    setNewImageUrl(url);
    setHasImageChanged(true);
  };

  const handleFileRevert = () => {
    setNewImageUrl('');
    setHasImageChanged(false);
  };

  function renderUploadStep() {
    return (
      <Stack gap={4}>
        <FilePondUploader
          maxFiles={1}
          label="Drag & Drop your new image"
          initialFileUrls={newImageUrl ? [newImageUrl] : []}
          onFileProcess={handleFileProcess}
          onFileRevert={handleFileRevert}
        />
      </Stack>
    );
  }

  function getPreviewEntity(): ItemModel | ListingModel | ArtistModel {
    const clonedEntity = Cloneable.deepCopy(entity);
    clonedEntity.image = newImageUrl;
    return clonedEntity;
  }

  function renderPreviewStep() {
    const previewEntity = getPreviewEntity();

    return (
      <Stack gap={4} alignItems="center">
        {entityType === 'item' && <ItemCard item={previewEntity as ItemModel} showMetadata={true} defaultMetadataExpanded={true} />}
        {entityType === 'listing' && <ItemCard item={previewEntity as ItemModel} />}
        {entityType === 'artist' && <ArtistCard artist={previewEntity as ArtistModel} />}
      </Stack>
    );
  }

  function renderWizardFinishingSuccessStep() {
    if (hasErrors) {
      return (
        <Stack gap={4} alignItems="center">
          <Icon as={AiFillCloseCircle} color="red.500" w={10} h={10} />
          <Heading size="xs" textAlign="center">
            Migration Failed.<br />Please Try Again
          </Heading>
        </Stack>
      );
    } else {
      return (
        <Stack gap={4} alignItems="center">
          <Icon as={AiFillCheckCircle} color="green.500" w={10} h={10} />
          <Heading size="xs">
            Image Migrated Successfully
          </Heading>
        </Stack>
      );
    }
  }

  const onWizardSubmit = () => {
    onSubmit(entity, newImageUrl);
  };

  const wizardSteps = [
    {
      label: 'Upload',
      content: renderUploadStep(),
      requiresValidation: true,
      isValidated: hasImageChanged
    },
    {
      label: 'Preview',
      content: renderPreviewStep()
    }
  ];

  return (
    <Stack gap={2} width="100%">
      <Wizard
        steps={wizardSteps}
        finishStep={renderWizardFinishingSuccessStep()}
        onSubmit={onWizardSubmit}
        loading={isLoading}
      />
    </Stack>
  );
}
